// @ts-nocheck
/**
 * アドベンチャーモード状態管理フック
 */

import { economyAccount, economyRequest, pendingBattleId } from '../../lib/economyClient';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type {
  AdventureProgress,
  NpcId,
  BattleResult,
  ChapterDisplayInfo,
  NpcDisplayInfo,
  BattleRewardResult,
  AdventureBattleConfig,
} from '../data/types';
import {
  CHAPTERS,
  getNpcConfig,
  getChapterConfig,
  starsToString,
} from '../data/constants';
import {
  loadProgress,
  saveProgress,
} from '../data/storage';
import {
  getProgress,
  recordBattle,
  getNpcRecord,
  isNpcCleared,
  isFirstBattle,
  getRemainingRewardCount,
  getClearProgress,
} from '../systems/progressManager';
import {
  isChapterUnlocked,
  getNpcUnlockStatus,
  getChapterProgress,
  getUnlockRequirementText,
} from '../systems/unlockChecker';
import {
  calculateBattleReward,
  applyReward,
  updateProgressAfterReward,
  applyAllClearBonus,
} from '../systems/rewardCalculator';
import { COMPETITORS, getDeck, expandDeckToCardIds } from '../../collection/tournament/data/competitors';
import {
  isAdventureServerAvailable,
  fetchAdventureProgress,
  syncAdventureProgress,
  recordBattleToServer,
  addTitleToServer,
} from './useAdventureServer';

// ========================================
// フックの型定義
// ========================================

export interface UseAdventureReturn {
  // 状態
  progress: AdventureProgress;
  selectedChapter: number;
  selectedNpc: NpcId | null;
  isDetailOpen: boolean;
  isResultOpen: boolean;
  lastBattleResult: BattleRewardResult | null;

  // 計算済みデータ
  chapters: ChapterDisplayInfo[];
  currentChapterNpcs: NpcDisplayInfo[];
  clearProgress: { cleared: number; total: number; percentage: number };

  // アクション
  selectChapter: (chapterId: number) => void;
  selectNpc: (npcId: NpcId) => void;
  closeDetail: () => void;
  startBattle: (npcId: NpcId) => AdventureBattleConfig | null;
  handleBattleEnd: (npcId: NpcId, result: BattleResult, playerData: any) => { playerData: any; reward: BattleRewardResult };
  closeResult: () => void;
  refreshProgress: () => void;
}

// ========================================
// メインフック
// ========================================

export function useAdventure(): UseAdventureReturn {
  // 進行データ
  const [progress, setProgress] = useState<AdventureProgress>(() => loadProgress());
  const [selectedChapter, setSelectedChapter] = useState<number>(1);
  const [selectedNpc, setSelectedNpc] = useState<NpcId | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isResultOpen, setIsResultOpen] = useState(false);
  const [lastBattleResult, setLastBattleResult] = useState<BattleRewardResult | null>(null);
  const [pendingBattleNpc, setPendingBattleNpc] = useState<NpcId | null>(null);

  // サーバー同期済みフラグ（初回のみ実行）
  const hasSyncedRef = useRef(false);

  // 初回マウント時にサーバーと同期
  useEffect(() => {
    if (hasSyncedRef.current) return;
    hasSyncedRef.current = true;

    const syncWithServer = async () => {
      if (!isAdventureServerAvailable()) {
        console.log('[useAdventure] Server not available, using local data');
        return;
      }

      try {
        const localProgress = loadProgress();
        const { progress: mergedProgress, resolution } = await syncAdventureProgress(localProgress);

        if (mergedProgress && resolution) {
          console.log('[useAdventure] Synced with server:', resolution);

          // サーバーのデータで更新
          setProgress(mergedProgress);
          saveProgress(mergedProgress);
        }
      } catch (error) {
        console.error('[useAdventure] Server sync failed:', error);
      }
    };

    syncWithServer();
  }, []);

  // 進行データの再読み込み
  const refreshProgress = useCallback(() => {
    setProgress(loadProgress());
  }, []);

  // クリア進捗
  const clearProgress = useMemo(() => getClearProgress(progress), [progress]);

  // 章データの構築
  const chapters = useMemo<ChapterDisplayInfo[]>(() => {
    return CHAPTERS.map((chapter) => {
      const chapterProgress = getChapterProgress(progress, chapter.id);
      const isUnlocked = isChapterUnlocked(progress, chapter.id);

      // 章内のNPC情報を構築
      const npcs: NpcDisplayInfo[] = chapter.npcs.map((npcId) => {
        const config = getNpcConfig(npcId);
        const competitor = COMPETITORS[npcId];
        const status = getNpcUnlockStatus(progress, npcId);
        const record = progress.npcRecords[npcId] || null;

        return {
          npcId,
          name: competitor?.name || npcId,
          portrait: competitor?.portrait || '❓',
          deckName: status === 'locked' ? '???' : (config?.deckName || '???'),
          difficultyStars: config?.difficultyStars || 1,
          tier: config?.tier || 'D',
          status,
          record,
          firstClearReward: config?.firstClearReward || { gold: 0, packs: [] },
          unlockRequirements: config?.unlockRequirements || [],
        };
      });

      return {
        id: chapter.id,
        name: chapter.name,
        description: chapter.description,
        isUnlocked,
        progress: {
          cleared: chapterProgress.cleared,
          total: chapterProgress.total,
        },
        npcs,
      };
    });
  }, [progress]);

  // 現在選択中の章のNPC一覧
  const currentChapterNpcs = useMemo(() => {
    const chapter = chapters.find((c) => c.id === selectedChapter);
    return chapter?.npcs || [];
  }, [chapters, selectedChapter]);

  // 章選択
  const selectChapter = useCallback((chapterId: number) => {
    if (isChapterUnlocked(progress, chapterId)) {
      setSelectedChapter(chapterId);
    }
  }, [progress]);

  // NPC選択（詳細モーダルを開く）
  const selectNpc = useCallback((npcId: NpcId) => {
    const status = getNpcUnlockStatus(progress, npcId);
    if (status !== 'locked') {
      setSelectedNpc(npcId);
      setIsDetailOpen(true);
    }
  }, [progress]);

  // 詳細モーダルを閉じる
  const closeDetail = useCallback(() => {
    setIsDetailOpen(false);
    setSelectedNpc(null);
  }, []);

  // 対戦開始
  const startBattle = useCallback((npcId: NpcId): AdventureBattleConfig | null => {
    const status = getNpcUnlockStatus(progress, npcId);
    if (status === 'locked') return null;

    const config = getNpcConfig(npcId);
    const competitor = COMPETITORS[npcId];
    if (!config || !competitor) return null;

    // デッキを取得
    let deckName = config.deckName;

    // 再戦時はランダムでデッキ2を使用する可能性
    if (isNpcCleared(progress, npcId) && competitor.decks.length > 1) {
      if (Math.random() < 0.5) {
        deckName = competitor.decks[1];
      }
    }

    // ドッペルゲンガーは特殊処理（後で実装）
    if (npcId === 'doppelganger') {
      deckName = 'プレイヤーコピー';
    }

    // デッキをカードIDに展開
    let deckCardIds: string[] = [];
    try {
      const deck = getDeck(deckName);
      if (!deck) {
        console.error('Deck not found:', deckName);
        return null;
      }
      deckCardIds = expandDeckToCardIds(deck);
    } catch (e) {
      console.error('Failed to expand deck:', deckName, e);
      return null;
    }

    setPendingBattleNpc(npcId);
    closeDetail();

    return {
      npcId,
      npcName: competitor.name,
      npcDeckName: deckName,
      npcDeckCardIds: deckCardIds,
      aiDifficulty: competitor.aiDifficulty || 'hard',
      isFirstBattle: isFirstBattle(progress, npcId),
    };
  }, [progress, closeDetail]);

  // 対戦終了処理
  // npcId を引数で受け取る（コールバック渡し時のクロージャ問題を回避）
  const handleBattleEnd = useCallback((
    npcId: NpcId,
    result: BattleResult,
    playerData: any
  ) => {
    if (!npcId) {
      return { playerData, reward: { gold: 0, packs: [], newUnlocks: [], isFirstClear: false } };
    }

    if (economyAccount()) return (async () => {
      const response = await economyRequest('adventure/battle', {
        npc_id: npcId, result, battle_id: pendingBattleId('adventure'),
      });
      saveProgress(response.progress);
      const reward = { gold: response.gold_reward, packs: response.pack_rewards,
        isFirstClear: response.is_first_clear, newUnlocks: [], title: response.titles?.[0],
        rewardLimited: response.reward_limited };
      setProgress(response.progress); setLastBattleResult(reward); setIsResultOpen(true); setPendingBattleNpc(null);
      return { playerData, reward };
    })();
    // 初回クリアかどうかを先に判定
    const wasFirstClear = !progress.clearedNpcs.includes(npcId) && result === 'win';

    // 報酬を計算
    const reward = calculateBattleReward(progress, npcId, result);

    // 対戦結果を記録（progressを更新）
    let updatedProgress = recordBattle(progress, npcId, result);

    // プレイヤーデータに報酬を付与
    let updatedPlayerData = playerData;
    if (result === 'win') {
      updatedPlayerData = applyReward(playerData, reward);
      updatedProgress = updateProgressAfterReward(updatedProgress, npcId, reward);

      // 全クリアボーナスのチェック
      const allClearResult = applyAllClearBonus(updatedPlayerData, updatedProgress);
      if (allClearResult) {
        updatedPlayerData = allClearResult.playerData;
        updatedProgress = allClearResult.progress;
        // ボーナス報酬を追加
        reward.gold += 20000;
        reward.packs.push({ type: 'royal', count: 3 });
        reward.title = 'マジックスピリット・マスター';
      }
    }

    // 状態を更新
    setProgress(updatedProgress);
    setLastBattleResult(reward);
    setIsResultOpen(true);
    setPendingBattleNpc(null);

    // サーバーに対戦結果と報酬を記録（バックグラウンド）
    if (isAdventureServerAvailable()) {
      // 勝利時のみ報酬を送信
      const goldReward = result === 'win' ? reward.gold : 0;
      const packRewards = result === 'win' ? reward.packs : [];

      recordBattleToServer(npcId, result, wasFirstClear, goldReward, packRewards)
        .then(({ progress: serverProgress, newGoldBalance, newUnopenedPacks }) => {
          if (serverProgress) {
            // サーバーの最新データで更新
            setProgress(serverProgress);
            saveProgress(serverProgress);
            console.log('[useAdventure] Battle recorded to server', {
              goldReward,
              packRewards,
              newGoldBalance,
              newUnopenedPacks,
            });
          }
        })
        .catch((error) => {
          console.error('[useAdventure] Failed to record battle to server:', error);
        });

      // 称号が獲得された場合はサーバーにも追加
      if (reward.title) {
        addTitleToServer(reward.title).catch(console.error);
      }
    }

    return { playerData: updatedPlayerData, reward };
  }, [progress]);

  // 結果モーダルを閉じる
  const closeResult = useCallback(() => {
    setIsResultOpen(false);
    setLastBattleResult(null);
  }, []);

  return {
    // 状態
    progress,
    selectedChapter,
    selectedNpc,
    isDetailOpen,
    isResultOpen,
    lastBattleResult,

    // 計算済みデータ
    chapters,
    currentChapterNpcs,
    clearProgress,

    // アクション
    selectChapter,
    selectNpc,
    closeDetail,
    startBattle,
    handleBattleEnd,
    closeResult,
    refreshProgress,
  };
}

// ========================================
// ヘルパーフック
// ========================================

/**
 * NPC詳細情報を取得
 */
export function useNpcDetail(npcId: NpcId | null, progress: AdventureProgress) {
  return useMemo(() => {
    if (!npcId) return null;

    const config = getNpcConfig(npcId);
    const competitor = COMPETITORS[npcId];
    if (!config || !competitor) return null;

    const status = getNpcUnlockStatus(progress, npcId);
    const record = getNpcRecord(progress, npcId);
    const remainingRewards = getRemainingRewardCount(progress, npcId);

    return {
      npcId,
      name: competitor.name,
      portrait: competitor.portrait,
      attribute: competitor.attribute,
      style: competitor.style,
      quotes: competitor.quotes,
      deckName: config.deckName,
      deckNames: competitor.decks,
      tier: config.tier,
      difficultyStars: config.difficultyStars,
      difficultyText: starsToString(config.difficultyStars),
      winRate: config.winRate,
      status,
      record,
      firstClearReward: config.firstClearReward,
      repeatReward: config.repeatReward,
      isFirstBattle: isFirstBattle(progress, npcId),
      remainingRewards,
      unlockRequirementText: getUnlockRequirementText(npcId),
    };
  }, [npcId, progress]);
}
